import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, CheckCircle2, XCircle, User, AtSign, KeyRound } from 'lucide-react'
import { AuthLayout } from '@/components/AuthLayout'
import {
  Button,
  Input,
  PasswordInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui'
import { OAuthButtons } from '@/components/OAuthButtons'
import { useSignup } from '@/hooks/auth'
import { signupSchema } from '@/lib/validations'
import type { SignupFormData } from '@/lib/validations'
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/segmented-control'

export default function Signup() {
  const signup = useSignup()

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      accountType: 'client',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  const password = form.watch('password')
  const confirmPassword = form.watch('confirmPassword')
  const passwordsMatch = password && confirmPassword && password === confirmPassword

  const onSubmit = async (data: SignupFormData) => {
    await signup.mutateAsync(data)
  }

  return (
    <AuthLayout
      title="Create an account"
      subtitle="Welcome to Masonyt. Let's get you started."
      footer={{
        text: 'Already have an account?',
        linkText: 'Sign in',
        linkTo: '/login',
      }}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="accountType"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <SegmentedControl
                    {...field}
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SegmentedControlItem value="client">I'm a Client</SegmentedControlItem>
                    <SegmentedControlItem value="developer">I'm a Developer</SegmentedControlItem>
                  </SegmentedControl>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-background px-4 text-muted-foreground">Continue with email</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input placeholder="yourusername" {...field} className="pl-10" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <AtSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        {...field}
                        className="pl-10"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder="Create a strong password"
                    showStrength={true}
                    icon={KeyRound}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <div className="space-y-2">
                    <PasswordInput placeholder="Confirm your password" icon={KeyRound} {...field} />
                    {confirmPassword && (
                      <div className="flex items-center gap-2 text-xs">
                        {passwordsMatch ? (
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                        ) : (
                          <XCircle className="h-3 w-3 text-red-500" />
                        )}
                        <span className={passwordsMatch ? 'text-green-500' : 'text-red-500'}>
                          Passwords match
                        </span>
                      </div>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting || signup.isPending}
          >
            {form.formState.isSubmitting || signup.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              'Create account'
            )}
          </Button>
        </form>
      </Form>
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
        </div>
      </div>
      <OAuthButtons />
    </AuthLayout>
  )
}
